import { useState, useRef, useEffect } from "react";
import { DateRange } from "react-date-range";
import { format, differenceInCalendarDays } from "date-fns";
import { fr } from "date-fns/locale";
import { FiCalendar, FiArrowRight } from "react-icons/fi";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";

function BookingCard({ checkIn, checkOut, setCheckIn, setCheckOut }) {
  const [open, setOpen] = useState(false);
  const ref = useRef();

  const [date, setDate] = useState([
    {
      startDate: checkIn ? new Date(checkIn) : new Date(),
      endDate: checkOut ? new Date(checkOut) : new Date(),
      key: "selection",
    },
  ]);

  useEffect(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (date[0].startDate < today) {
      setDate([{ startDate: today, endDate: today, key: "selection" }]);
    }
  }, []);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleApply = () => {
    const start = date[0].startDate;
    const end = date[0].endDate;
    setCheckIn(format(start, "yyyy-MM-dd"));
    setCheckOut(format(end, "yyyy-MM-dd"));
    setOpen(false);
  };

  const nights =
    checkIn && checkOut
      ? differenceInCalendarDays(new Date(checkOut), new Date(checkIn))
      : null;

  const formatDisplay = (d) =>
    format(new Date(d), "d MMM", { locale: fr });

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-md overflow-hidden">

      {/* ── Header card ─────────────────────────────────────────── */}
      <div className="px-5 pt-5 pb-4 border-b border-gray-100">
        <p className="text-[13px] font-medium text-gray-500 uppercase tracking-wider">
          Disponibilité
        </p>
        <h3 className="text-lg font-semibold text-gray-900 mt-0.5">
          {checkIn && checkOut
            ? `${nights} nuit${nights > 1 ? "s" : ""}`
            : "Choisissez vos dates"}
        </h3>
        {checkIn && checkOut && (
          <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
            <span>{formatDisplay(checkIn)}</span>
            <FiArrowRight className="text-gray-300" />
            <span>{formatDisplay(checkOut)}</span>
          </div>
        )}
      </div>

      {/* ── Sélecteur dates ──────────────────────────────────────── */}
      <div className="px-5 py-4" ref={ref}>

        {/* Trigger */}
        <button
          onClick={() => setOpen(!open)}
          className={`
            w-full flex items-center gap-3 px-4 py-3 rounded-xl border text-sm transition
            ${open
              ? "border-[#3A9D23] ring-2 ring-[#3A9D23]/10"
              : "border-gray-200 hover:border-gray-300"
            }
          `}
        >
          <FiCalendar className="text-gray-400 shrink-0" />
          <span className={checkIn && checkOut ? "text-gray-800" : "text-gray-400"}>
            {checkIn && checkOut
              ? `${formatDisplay(checkIn)} → ${formatDisplay(checkOut)}`
              : "Sélectionner vos dates"}
          </span>
        </button>

        {/* Calendrier */}
        {open && (
          <div className="mt-2 rounded-xl border border-gray-100 shadow-lg overflow-hidden bg-white">
            <DateRange
              ranges={date}
              onChange={(item) => setDate([item.selection])}
              moveRangeOnFirstSelection={false}
              rangeColors={["#3A9D23"]}
              months={1}
              direction="vertical"
              showDateDisplay={false}
              minDate={new Date()}
              locale={fr}
            />
            <div className="flex justify-end gap-2 px-4 pb-4">
              <button
                onClick={() => setOpen(false)}
                className="px-4 py-2 text-sm text-gray-500 hover:text-gray-700 transition"
              >
                Annuler
              </button>
              <button
                onClick={handleApply}
                className="px-5 py-2 text-sm rounded-lg bg-[#3A9D23] text-white hover:bg-[#33891E] transition font-medium"
              >
                Valider
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── CTA principal ────────────────────────────────────────── */}
      <div className="px-5 pb-5">
        <button
          onClick={() => {
            const roomsEl = document.getElementById("rooms");
            if (roomsEl) {
              window.scrollTo({
                top: roomsEl.getBoundingClientRect().top + window.pageYOffset - 100,
                behavior: "smooth",
              });
            }
          }}
          className="w-full py-3.5 rounded-xl bg-[#3A9D23] text-white text-sm font-semibold hover:bg-[#33891E] active:scale-[0.98] transition-all"
        >
          {checkIn && checkOut ? "Voir les chambres disponibles" : "Rechercher"}
        </button>

        {checkIn && checkOut && (
          <p className="text-xs text-center text-green-600 mt-2.5 flex items-center justify-center gap-1">
            <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
            Disponibilités mises à jour
          </p>
        )}
      </div>

    </div>
  );
}

export default BookingCard;